import React, { useState, useEffect, useRef } from "react";

/**
 * An avatar that survives a dead image URL.
 *
 * A missing picture and a broken one are not the same thing. Most of our avatar
 * code only handled the first: it checked whether a URL existed and fell back to
 * initials when it did not. But a URL that exists and 404s still renders an
 * <img>, which the browser draws as an empty box with the alt text spilling out
 * of its frame - the "broken circle" on the creators page.
 *
 * This renders the initial up front and only reveals the image once the browser
 * confirms it loaded, so a dead URL degrades to the same thing as no URL at all.
 * The image is layered over the initial rather than swapped in, so there is no
 * flash of empty space while it loads.
 *
 * @param {string} src       image URL; may be absent, or present and dead
 * @param {string} name      used for the initial and the alt text
 * @param {string} className applied to both the image and the fallback, so the
 *                           two are always the same size and shape
 */
export default function Avatar({ src, name = "", className = "", ...rest }) {
  const imgRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // A changed src is a fresh attempt: clear the previous verdict, or a new
  // picture would stay hidden behind an old failure.
  useEffect(() => {
    setFailed(false);
    setLoaded(false);
  }, [src]);

  // A cached image can finish loading before React attaches onLoad, so that
  // event never fires and a perfectly good picture would stay transparent.
  // naturalWidth is the browser's own record of "this decoded"; check it once
  // mounted, after every src change.
  useEffect(() => {
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) setLoaded(true);
  }, [src]);

  const label = (name || "").trim();
  const initial = label ? label.charAt(0).toUpperCase() : "?";
  const showImage = src && !failed;

  return (
    <>
      {/* The initial sits underneath and is covered once the image paints.
          It stays mounted rather than being swapped out, so nothing shifts. */}
      {(!showImage || !loaded) && (
        <div className={`${className} avatar-fallback`} aria-hidden="true">
          <span>{initial}</span>
        </div>
      )}
      {showImage && (
        <img
          ref={imgRef}
          className={className}
          src={src}
          alt={label || "Avatar"}
          onError={() => setFailed(true)}
          onLoad={() => setLoaded(true)}
          /* Hidden with opacity, never display:none. A display:none image can
             be skipped by the browser's lazy loader, which would leave a
             perfectly good picture invisible forever - worse than the bug this
             component fixes. Opacity keeps it laid out and loading, just
             transparent until it has actually painted. */
          style={{
            opacity: loaded ? 1 : 0,
            position: loaded ? undefined : "absolute",
            pointerEvents: loaded ? undefined : "none",
          }}
          {...rest}
        />
      )}
    </>
  );
}
